import {
  EPushNoticationPublishType,
  ICommonSettings,
  IImageLibraryDto,
  IPushNotificationSetting
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const PUSH_NOTIFICATION_SEARCH_TERMS: string[] = ["Team Id", "Publish Type", "Notification Certificate"];

export interface IConfigPushNotificationState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingPushNotification?: IPushNotificationSetting;
  allImage?: IImageLibraryDto[];
  imageSearchText: string;
  fileName: string;
  publishType: EPushNoticationPublishType;
}

export interface IConfigPushNotificationProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingPushNotificationId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigPushNotification extends React.PureComponent<
  IConfigPushNotificationProps,
  IConfigPushNotificationState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigPushNotificationProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingPushNotification: undefined,
      allImage: [],
      imageSearchText: "",
      fileName: "",
      publishType: EPushNoticationPublishType.Development
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigPushNotificationProps & IWithSDProps) {
    if (prevProps.editingPushNotificationId !== this.props.editingPushNotificationId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingPushNotificationId) {
      const pn = this.props.commonSettings.pushNotificationSettings?.find(
        (x) => x.id === this.props.editingPushNotificationId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState({ editingPushNotification: pn }, () => {
        if (pn) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.pushNotificationName = pn.name;
          fieldsValue.teamId = pn.teamId;
          fieldsValue.notificationCertificate = pn.notificationCertificate;

          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({
            fileName: pn.notificationCertificate,
            publishType: pn.publishType
          });
        }
      });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const setting = this.props.commonSettings.pushNotificationSettings;

    const isSettingAlreadyExist =
      setting?.some(
        (x) =>
          x.name === value && x.id !== this.props.editingPushNotificationId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Push Notification Setting name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IPushNotificationSetting;
    if (this.props.editingPushNotificationId) {
      obj = {
        id: this.props.editingPushNotificationId,
        name: data.pushNotificationName,
        publishType: this.state.publishType,
        teamId: data.teamId?.trim(),
        notificationCertificate: this.state.fileName
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "pushNotificationSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.pushNotificationName,
        publishType: this.state.publishType,
        teamId: data.teamId?.trim(),
        notificationCertificate: this.state.fileName
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "pushNotificationSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deletePushNotification = () => {
    if (!this.props.editingPushNotificationId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingPushNotificationId || "",
      configKey: "pushNotificationSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Push Notification setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Push notification setting"
        secondHeader="Push notification setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingPushNotificationId ? this.deletePushNotification : undefined}
            deleteTestId="iosPushNotificationSettingModalDeleteButton"
            submitTestId={`iosPushNotificationSettingModal${
              this.props.editingPushNotificationId ? "Update" : "Create"
            }Button`}
            submitLabel={this.props.editingPushNotificationId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configPushNotificationForm"
          />
        }
      >
        <Form
          id="configPushNotificationForm"
          name="pushNotificationSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              "pushnotificationsettingspopup_pushnotificationsettings_description"
            )}
          >
            <ConfigField
              label="Name"
              required
              tooltipContentKey="pushnotificationsettingspopup_pushnotificationsettings_name"
            >
              <Form.Item
                name="pushNotificationName"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Push Notification Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Push Notification Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="iosPushNotificationSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Team Id"
              required
              tooltipContentKey="pushnotificationsettingspopup_pushnotificationsettings_teamid"
            >
              <Form.Item name="teamId" rules={[{ required: true, message: "Please enter a Team Id." }]}>
                <KuikaInput size="md" data-testid="iosPushNotificationSettingTeamIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Publish Type"
              tooltipContentKey="pushnotificationsettingspopup_pushnotificationsettings_publishtype"
            >
              <Form.Item name="publishType">
                <KuikaSelectbox
                  size="md"
                  defaultValue={this.state.publishType}
                  value={this.state.publishType}
                  onChange={(e) => this.setState({ publishType: e })}
                  options={[
                    { value: EPushNoticationPublishType.Development, label: "Development" },
                    { value: EPushNoticationPublishType.Production, label: "Production" }
                  ]}
                />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Notification Certificate"
              tooltipContentKey="pushnotificationsettingspopup_pushnotificationsettings_notificationcertificate"
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".p8"
                getImageLibrary={this.getImageLibrary}
                selectedId={this.state.fileName}
                fileName={this.state.fileName}
                onSelect={(image: IImageLibraryDto): void => this.setState({ fileName: image.fileName })}
                onClear={(): void => this.setState({ fileName: "" })}
              />
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
